// ---------------------------------------------------------------
// Shared session config + founder/registration helpers for the
// /roundtable · /register · /activate-node trio.
//
// Sessions live in one place so the Register gate, the countdown
// timer, and the email template all agree.
// ---------------------------------------------------------------

// EDT = UTC-4. All three broadcast windows now run on
// SUNDAY, AUG 16 back-to-back — 10:30 AM · 2:00 PM · 6:00 PM.
export const ROUNDTABLE_SESSIONS = [
  {
    id: "session-1",
    label: "Session 1",
    dayLabel: "Sunday",
    dateLabel: "Aug 16, 2026",
    timeLabel: "10:30 AM EDT",
    iso: "2026-08-16T10:30:00-04:00",
  },
  {
    id: "session-2",
    label: "Session 2",
    dayLabel: "Sunday",
    dateLabel: "Aug 16, 2026",
    timeLabel: "2:00 PM EDT",
    iso: "2026-08-16T14:00:00-04:00",
  },
  {
    id: "session-3",
    label: "Session 3",
    dayLabel: "Sunday",
    dateLabel: "Aug 16, 2026",
    timeLabel: "6:00 PM EDT",
    iso: "2026-08-16T18:00:00-04:00",
  },
];

// Session is "live/passed" if within 60 min before start OR past.
export function isSessionLiveOrPast(iso, now = new Date()) {
  const start = new Date(iso).getTime();
  const nowMs = now.getTime();
  return nowMs >= start - 60 * 60 * 1000;
}

// -----------------------------------------------------------
// Founder access · read URL first, fall back to sessionStorage
// so the founder badge survives navigation between routes even
// after the query param is stripped.
// -----------------------------------------------------------
const FOUNDER_KEY = "wa_founder_access";

export function isFounder() {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("founder") === "true") {
      window.sessionStorage.setItem(FOUNDER_KEY, "true");
      return true;
    }
    return window.sessionStorage.getItem(FOUNDER_KEY) === "true";
  } catch {
    return false;
  }
}

// Preserve `founder=true` when we build outbound links from one
// gated page to another. Returns a query string prefixed with
// "?" (or "" when not needed).
export function founderQS(base = "") {
  const q = base ? new URLSearchParams(base) : new URLSearchParams();
  if (isFounder()) q.set("founder", "true");
  const s = q.toString();
  return s ? `?${s}` : "";
}

// -----------------------------------------------------------
// Registration state · used by the /roundtable soft gate so
// visitors who came through /register don't get bounced back.
// -----------------------------------------------------------
const REG_KEY = "wa_roundtable_registration";

export function saveRegistration(payload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(REG_KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
}

export function getRegistration() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(REG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasRegistered() {
  return !!getRegistration();
}

// -----------------------------------------------------------
// Council Induction (Grand Opening) — 22-second cinematic
// that plays at the top of /roundtable. We flip this flag the
// moment the visitor has watched it once (or dismissed it),
// so returning visitors get a compact "Replay" button instead
// of the auto-play hero. Session-scoped by design.
// -----------------------------------------------------------
const INDUCTION_KEY = "wa_induction_watched";

export function markInductionWatched() {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(INDUCTION_KEY, "true"); } catch { /* ignore */ }
}

export function hasWatchedInduction() {
  if (typeof window === "undefined") return false;
  try { return window.sessionStorage.getItem(INDUCTION_KEY) === "true"; } catch { return false; }
}
